import React, { useMemo } from 'react';
import { Drawer } from '@bedrock/components';
import { Z_INDEX } from '#utils/zIndex';

export interface RechargeDrawerProps {
  visible: boolean;
  onClose: () => void;
  /** 标题主行（文本/节点），不传则用 orderNo 拼「充值单 #单号」 */
  title?: React.ReactNode;
  /** 单号（缺省标题用） */
  orderNo?: string;
  /** 标题主行下方附加内容（如需求名/品类标签） */
  titleExtra?: React.ReactNode;
  /** 底部操作按钮（已统一 justify-end 容器，按钮用 rechargeFooterButtonProps 统一 size/最小宽） */
  footer?: React.ReactNode;
  children?: React.ReactNode;
  /** 是否禁止点遮罩关闭（发起页 BUDGET_DASHBOARD 场景传 false 外的场景按 Drawer 默认） */
  maskClosable?: boolean;
  /** Drawer getContainer（发起页 BUDGET_DASHBOARD 嵌入父抽屉用 false） */
  getContainer?: false;
}

/** 底部操作按钮统一样式（size + 最小宽），供发起/处理页 footer 按钮复用 */
export const rechargeFooterButtonProps = {
  size: 'large' as const,
  className: 'min-w-[120px]',
};

/**
 * 充值发起/处理共用 Drawer 骨架：灰底（bodyClassName bg-[#F0F1F2]）+ 白卡片
 * （rounded-lg border bg-white p-6）+ 标题/底部按钮统一样式。
 *
 * 发起页（RechargeFormView）与处理页（RechargeConfirmView）复用本容器，
 * 样式改动只改此处，两个页面同时生效（字段/按钮逻辑各自保留在视图内）。
 */
const RechargeDrawer: React.FC<RechargeDrawerProps> = ({
  visible,
  onClose,
  title,
  orderNo,
  titleExtra,
  footer,
  children,
  maskClosable,
  getContainer,
}) => {
  const drawerWidth = useMemo(() => {
    const width80 = window.innerWidth * 0.8;
    return width80 > 960 ? width80 : 960;
  }, []);

  return (
    <Drawer
      visible={visible}
      onClose={onClose}
      width={drawerWidth}
      bodyClassName="bg-[#F0F1F2]"
      style={{ right: 0 }}
      zIndex={Z_INDEX.drawer}
      maskStyle={{ backgroundColor: 'transparent' }}
      showMask
      destroyOnClose
      maskClosable={maskClosable}
      getContainer={getContainer}
      title={
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-2">
            <span className="text-lg font-semibold leading-[26px]">
              {title ?? `充值单 #${orderNo || ''}`}
            </span>
            {titleExtra}
          </div>
        </div>
      }
      footer={
        footer ? (
          <div className="flex items-center justify-end gap-2">{footer}</div>
        ) : undefined
      }
    >
      <div className="flex flex-col gap-4 bg-[#f5f5f5]">
        <div className="rounded-lg border border-[#e5e7eb] bg-white p-6">
          {children}
        </div>
      </div>
    </Drawer>
  );
};

export default RechargeDrawer;
